/**
 * Logger Service for Northstar Fuel Application
 * Logs events to MongoDB via API endpoint
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal'
}

export enum LogCategory {
  AUTH = 'auth',
  API = 'api',
  UI = 'ui',
  NAVIGATION = 'navigation',
  MAP = 'map',
  TRANSACTION = 'transaction',
  USER_ACTION = 'user_action',
  ANNOUNCEMENT = 'announcement',
  SYSTEM = 'system',
  SECURITY = 'security'
}

export interface LogEntry {
  level: LogLevel;
  category: LogCategory;
  message: string;
  userId?: string;
  startCode?: string;
  metadata?: Record<string, any>;
  userAgent?: string;
  url?: string;
  timestamp?: Date;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

class LoggerService {
  private readonly API_ENDPOINT = '/api/logs';
  private readonly BATCH_ENDPOINT = '/api/logs/batch';
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000;
  private logQueue: LogEntry[] = [];
  private isProcessingQueue = false;
  private batchSize = 5;
  private flushInterval = 8000; // 8 seconds
  private flushTimer: NodeJS.Timeout | null = null;
  private readonly MAX_LOG_MESSAGE_LENGTH = 400;
  private readonly MAX_METADATA_STRING_LENGTH = 250;
  private readonly MAX_BATCH_PAYLOAD_BYTES = 24 * 1024;
  private recentlyQueued = new Set<string>();

  constructor() {
    // Start periodic flush
    this.startFlushTimer();
    
    // Flush logs before page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  private startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  private getUserInfo(): { userId?: string; startCode?: string } {
    try {
      const userStr = localStorage.getItem('quikq_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          userId: user.userId,
          startCode: user.startCode
        };
      }
    } catch (error) {
      // Silently fail - user info is optional
    }
    return {};
  }

  private createLogEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    metadata?: Record<string, any>,
    error?: Error
  ): LogEntry {
    const userInfo = this.getUserInfo();
    const entry: LogEntry = {
      level,
      category,
      message: (message || '').slice(0, this.MAX_LOG_MESSAGE_LENGTH),
      ...userInfo,
      metadata: this.sanitizeMetadata(metadata),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.pathname : undefined,
      timestamp: new Date()
    };

    if (error) {
      entry.error = {
        name: error.name,
        message: (error.message || '').slice(0, this.MAX_LOG_MESSAGE_LENGTH),
        stack: error.stack ? error.stack.slice(0, 1000) : undefined
      };
    }

    return entry;
  }

  private sanitizeMetadata(metadata?: Record<string, any>): Record<string, any> | undefined {
    if (!metadata) return undefined;
    const sanitized: Record<string, any> = {};
    Object.entries(metadata).forEach(([key, value]) => {
      if (value == null) return;
      if (typeof value === 'string') {
        sanitized[key] = value.slice(0, this.MAX_METADATA_STRING_LENGTH);
      } else if (Array.isArray(value)) {
        sanitized[key] = value.slice(0, 20).map((item) =>
          typeof item === 'string' ? item.slice(0, this.MAX_METADATA_STRING_LENGTH) : item
        );
      } else {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  private async sendLog(entry: LogEntry, retryCount = 0): Promise<void> {
    try {
      const response = await fetch(this.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      // Retry logic
      if (retryCount < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * (retryCount + 1)));
        return this.sendLog(entry, retryCount + 1);
      }
      
      // If all retries failed, log to console as fallback
      console.error('Failed to send log to server:', entry, error);
    }
  }

  private async sendBatch(entries: LogEntry[], retryCount = 0): Promise<void> {
    try {
      const response = await fetch(this.BATCH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logs: entries })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      if (retryCount < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * (retryCount + 1)));
        return this.sendBatch(entries, retryCount + 1);
      }

      console.error('Failed to send log batch to server:', error);
    }
  }

  private addToQueue(entry: LogEntry): void {
    const dedupeKey = `${entry.level}|${entry.category}|${entry.message}`;
    if (this.recentlyQueued.has(dedupeKey)) {
      return;
    }
    this.recentlyQueued.add(dedupeKey);
    setTimeout(() => this.recentlyQueued.delete(dedupeKey), 2000);

    this.logQueue.push(entry);
    
    // If queue is getting large, flush immediately
    if (this.logQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  private async flush(): Promise<void> {
    if (this.isProcessingQueue || this.logQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    try {
      // Send logs in batches
      for (let i = 0; i < logsToSend.length; ) {
        const batch: LogEntry[] = [];
        let payloadBytes = 0;
        while (i < logsToSend.length && batch.length < this.batchSize) {
          const next = logsToSend[i];
          const nextBytes = JSON.stringify(next).length;
          if (batch.length > 0 && payloadBytes + nextBytes > this.MAX_BATCH_PAYLOAD_BYTES) {
            break;
          }
          batch.push(next);
          payloadBytes += nextBytes;
          i++;
        }
        if (batch.length > 0) {
          await this.sendBatch(batch);
        }
      }
    } catch (error) {
      // If flush fails, put logs back in queue
      this.logQueue.unshift(...logsToSend);
      console.error('Failed to flush logs:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  // Public logging methods
  public debug(category: LogCategory, message: string, metadata?: Record<string, any>): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, category, message, metadata);
    this.addToQueue(entry);
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[${category}]`, message, metadata);
    }
  }

  public info(category: LogCategory, message: string, metadata?: Record<string, any>): void {
    const entry = this.createLogEntry(LogLevel.INFO, category, message, metadata);
    this.addToQueue(entry);
    if (process.env.NODE_ENV === 'development') {
      console.info(`[${category}]`, message, metadata);
    }
  }

  public warn(category: LogCategory, message: string, metadata?: Record<string, any>): void {
    const entry = this.createLogEntry(LogLevel.WARN, category, message, metadata);
    this.addToQueue(entry);
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && (window as any).NSF_DEBUG)) {
      console.warn(`[${category}]`, message, metadata);
    }
  }

  public error(category: LogCategory, message: string, error?: Error, metadata?: Record<string, any>): void {
    const entry = this.createLogEntry(LogLevel.ERROR, category, message, metadata, error);
    this.addToQueue(entry);
    // Only log to console in development or when explicit debug flag is enabled
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && (window as any).NSF_DEBUG)) {
      console.error(`[${category}]`, message, error, metadata);
    }
  }

  public fatal(category: LogCategory, message: string, error?: Error, metadata?: Record<string, any>): void {
    const entry = this.createLogEntry(LogLevel.FATAL, category, message, metadata, error);
    this.addToQueue(entry);
    // Flush immediately for fatal errors
    this.flush();
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && (window as any).NSF_DEBUG)) {
      console.error(`[FATAL][${category}]`, message, error, metadata);
    }
  }

  // Convenience methods for common operations
  public logLogin(userId: string, success: boolean, error?: Error): void {
    this.info(LogCategory.AUTH, `Login ${success ? 'successful' : 'failed'}`, {
      userId,
      success,
      error: error?.message
    });
  }

  public logLogout(userId?: string): void {
    this.info(LogCategory.AUTH, 'User logged out', { userId });
  }

  public logApiCall(endpoint: string, method: string, success: boolean, statusCode?: number, error?: Error): void {
    const category = success ? LogLevel.INFO : LogLevel.ERROR;
    const message = `API ${method} ${endpoint} ${success ? 'success' : 'failed'}`;
    
    if (success) {
      this.info(LogCategory.API, message, { endpoint, method, statusCode });
    } else {
      this.error(LogCategory.API, message, error, { endpoint, method, statusCode });
    }
  }

  public logNavigation(from: string, to: string): void {
    this.debug(LogCategory.NAVIGATION, `Navigation: ${from} -> ${to}`, { from, to });
  }

  public logUserAction(action: string, metadata?: Record<string, any>): void {
    this.info(LogCategory.USER_ACTION, action, metadata);
  }

  // Force flush logs (useful before critical operations)
  public async forceFlush(): Promise<void> {
    await this.flush();
  }
}

// Export singleton instance
export const logger = new LoggerService();






